import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, PencilIcon, XIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { IconButton } from 'slab'
import { getGetQuotingQuoteResultsQuoteIdQueryKey, usePatchQuotingProspectsProspectIdQuotesId } from 'utils/web_api'
import { type QuotingQuote } from 'utils/web_api.schemas'

import { QuoteExplorerMode } from './QuoteExplorer'
import { Input } from '../ui/input'

type EditableQuoteTitleProps = {
  quote: QuotingQuote | undefined
  prospectId: string
  quoteId: string
  prospectName: string
  mode: QuoteExplorerMode
  isLoading: boolean
}

export const EditableQuoteTitle = ({
  quote,
  prospectId,
  quoteId,
  prospectName,
  mode,
  isLoading,
}: EditableQuoteTitleProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const queryClient = useQueryClient()

  const quoteDate = quote?.created_at
    ? new Date(quote.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
    : null
  const defaultName = `${prospectName} quote ${quoteDate ?? ''}`
  const [editValue, setEditValue] = useState(quote?.name || defaultName)
  const [isSaving, setIsSaving] = useState(false)
  const quoteName = quote?.name || defaultName
  const isExploreMode = mode === QuoteExplorerMode.explore

  useEffect(() => {
    if (quote?.name) {
      setEditValue(quote.name)
    } else {
      setEditValue(defaultName)
    }
  }, [quote])

  const updateQuoteMutation = usePatchQuotingProspectsProspectIdQuotesId()
  const handleSave = async () => {
    if (!editValue.trim()) {
      toast.error('Quote name cannot be empty')
      return
    }

    setIsSaving(true)
    try {
      await updateQuoteMutation.mutateAsync({
        prospectId,
        id: quoteId,
        data: { name: editValue.trim() }
      })

      queryClient.invalidateQueries({
        queryKey: getGetQuotingQuoteResultsQuoteIdQueryKey(quoteId)
      })

      setIsEditing(false)
      toast.success('Quote name updated')
    } catch {
      toast.error('Failed to update quote name')
      setEditValue(quote?.name || defaultName)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(quote?.name || defaultName)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  if (isLoading) {
    return (
      <SkeletonItem heightClass='h-7' widthClass='w-48' />
    )
  }

  if (isExploreMode) {
    return (
      <h1 className='text-2xl'>
        {quoteName}
      </h1>
    )
  }

  if (isEditing) {
    return (
      <div className='flex items-center gap-2'>
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className='text-2xl font-semibold h-10 px-3'
          autoFocus
          disabled={isSaving}
        />
        <div className='flex items-center gap-1'>
          <IconButton
            icon={CheckIcon}
            label='Save'
            onClick={handleSave}
            disabled={isSaving}
            variant='primary'
          />
          <IconButton
            icon={XIcon}
            label='Cancel'
            onClick={handleCancel}
            disabled={isSaving}
            variant='secondary'
          />
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-center gap-2 group'>
      <h1 className='text-2xl'>
        Edit {quoteName}
      </h1>
      <div className='opacity-0 group-hover:opacity-100 transition-opacity'>
        <IconButton
          icon={PencilIcon}
          label='Edit quote name'
          onClick={() => setIsEditing(true)}
          variant='default'
        />
      </div>
    </div>
  )
}
